"use client"

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react"
import { createClient } from "@/lib/supabase/client"
import { useRouter } from "next/navigation"
import type { User } from "@supabase/supabase-js"
import { getLogger } from "@/lib/logger"
import { updateUserId } from "@/components/otel-browser"
import {
  isExpectedUnauthenticatedAuthError,
  isRecoverableAuthSessionError,
  isTransientAuthServiceError,
} from "@/lib/supabase/auth-session-recovery"

const log = getLogger('components.hooks.use-auth')
const AUTH_REFRESH_RETRY_DELAYS_MS = [100, 250, 500, 1_000, 2_000] as const

interface AuthContextType {
  user: User | null
  isLoading: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  // Memoize the Supabase client to avoid creating new instances on re-render
  const supabase = useMemo(() => createClient(), [])

  const clearClientAuthState = useCallback(() => {
    setUser(null)
    updateUserId(null)
  }, [])

  const recoverClientSession = useCallback(async (reason: unknown) => {
    log.warn("Recovering stale client auth session", {
      message: reason instanceof Error ? reason.message : String(reason),
    })
    try {
      await supabase.auth.signOut()
    } catch (signOutError) {
      log.warn("Local sign-out after auth recovery failed", {
        message: signOutError instanceof Error ? signOutError.message : String(signOutError),
      })
    } finally {
      clearClientAuthState()
    }
  }, [clearClientAuthState, supabase])

  const refreshUser = useCallback(async () => {
    try {
      let nextUser: User | null = null
      let error: unknown = null

      for (let attempt = 0; attempt <= AUTH_REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
        const result = await supabase.auth.getUser()
        nextUser = result.data.user
        error = result.error

        if (!isTransientAuthServiceError(error) || attempt === AUTH_REFRESH_RETRY_DELAYS_MS.length) {
          break
        }

        await wait(AUTH_REFRESH_RETRY_DELAYS_MS[attempt]!)
      }

      if (error) {
        if (isRecoverableAuthSessionError(error)) {
          await recoverClientSession(error)
          return
        }
        if (isExpectedUnauthenticatedAuthError(error)) {
          clearClientAuthState()
          return
        }
        if (isTransientAuthServiceError(error)) {
          log.warn("auth refresh hit transient service pressure after retries", {
            message: error instanceof Error ? error.message : String(error),
          })
          return
        }
        throw error
      }

      setUser(nextUser)
      updateUserId(nextUser?.id ?? null)
    } catch (error) {
      if (isTransientAuthServiceError(error)) {
        log.warn("auth refresh hit transient service pressure after retries", {
          message: error instanceof Error ? error.message : String(error),
        })
        return
      }
      log.error("refreshing user failed", error as Error)
      clearClientAuthState()
    }
  }, [clearClientAuthState, recoverClientSession, supabase])

  useEffect(() => {
    // Get initial user and set up auth state listener.
    // Token refresh is handled by proxy.ts on every request, so we only
    // need to keep React state in sync here.
    let ignore = false

    const init = async () => {
      await refreshUser()
      if (!ignore) setIsLoading(false)
    }
    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = session?.user ?? null
      setUser(newUser)
      // Update telemetry user context when auth state changes
      updateUserId(newUser?.id ?? null)
    })

    return () => {
      ignore = true
      subscription.unsubscribe()
    }
  }, [supabase, refreshUser])

  const signOut = async () => {
    await supabase.auth.signOut()
    clearClientAuthState()
    router.push("/")
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
